import { fetchGetAllRoles, fetchGetApplication, fetchOverviewChannels } from '@/service/api';

export type AppStatOption = {
  label: string | undefined;
  value: string;
};

let appStatOptionsCache: { apps: AppStatOption[]; channels: AppStatOption[] } | null = null;
let appStatOptionsInflight: Promise<{ apps: AppStatOption[]; channels: AppStatOption[] }> | null = null;

let rolesCache: Api.SystemManage.AllRole[] | null = null;
let rolesInflight: Promise<Api.SystemManage.AllRole[]> | null = null;

export function normalizeChannelList(channels: unknown): string[] {
  if (Array.isArray(channels)) {
    return channels
      .map(item => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          const record = item as Record<string, unknown>;
          return String(record.label ?? record.name ?? record.channel ?? record.appName ?? record.id ?? '');
        }
        return '';
      })
      .filter(Boolean);
  }

  if (channels && typeof channels === 'object') {
    const record = channels as Record<string, unknown>;
    for (const key of ['channels', 'records', 'list', 'data']) {
      if (Array.isArray(record[key])) {
        return normalizeChannelList(record[key]);
      }
    }
  }

  return [];
}

function extractChannelApiData(payload: unknown): string[] {
  if (!payload) return [];

  if (Array.isArray(payload)) {
    return normalizeChannelList(payload);
  }

  if (typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    if ('data' in record) {
      return normalizeChannelList(record.data);
    }
    return normalizeChannelList(payload);
  }

  return [];
}

async function fetchChannelList() {
  const { error: allError, data: allData } = await fetchOverviewChannels('all');
  if (!allError) {
    const channels = extractChannelApiData(allData);
    if (channels.length) return channels;
  }

  const { error, data } = await fetchOverviewChannels('authority');
  if (error) return [];
  return extractChannelApiData(data);
}

function mapAppOptions(data: Api.Application.App[]): AppStatOption[] {
  return data.map(item => ({
    label: (item.appName || item.label || item.id) as string,
    value: String(item.id)
  }));
}

function mapChannelOptions(channels: string[]): AppStatOption[] {
  return channels.map(channel => ({
    label: channel,
    value: channel
  }));
}

/** 角色列表（会话内缓存 + 并发去重） */
export async function loadAllRoles(force = false) {
  if (!force && rolesCache) {
    return rolesCache;
  }

  if (!force && rolesInflight) {
    return rolesInflight;
  }

  rolesInflight = fetchGetAllRoles()
    .then(({ error, data }) => {
      rolesInflight = null;
      if (error || !data) {
        return rolesCache ?? [];
      }
      rolesCache = data;
      return data;
    })
    .catch(() => {
      rolesInflight = null;
      return rolesCache ?? [];
    });

  return rolesInflight;
}

export function toRoleCodeToNameMap(roles: Api.SystemManage.AllRole[]) {
  return Object.fromEntries(roles.map(item => [item.roleCode, item.name]));
}

export function toRoleSelectOptions(roles: Api.SystemManage.AllRole[]): CommonType.Option<string>[] {
  return roles.map(item => ({
    label: item.name,
    value: item.roleCode
  }));
}

export function invalidateAllRoles() {
  rolesCache = null;
  rolesInflight = null;
}

/** 数据浏览权限弹窗：应用 + 渠道选项（会话内缓存 + 并发去重） */
export async function loadAppStatPermissionOptions(force = false) {
  if (!force && appStatOptionsCache) {
    return appStatOptionsCache;
  }

  if (!force && appStatOptionsInflight) {
    return appStatOptionsInflight;
  }

  appStatOptionsInflight = Promise.all([
    fetchGetApplication(),
    fetchChannelList()
  ])
    .then(([appResult, channels]) => {
      appStatOptionsInflight = null;

      const apps =
        !appResult.error && Array.isArray(appResult.data) ? mapAppOptions(appResult.data) : [];

      const options = {
        apps,
        channels: mapChannelOptions(channels)
      };

      appStatOptionsCache = options;
      return options;
    })
    .catch(() => {
      appStatOptionsInflight = null;
      return appStatOptionsCache ?? { apps: [], channels: [] };
    });

  return appStatOptionsInflight;
}

export function invalidateAppStatPermissionOptions() {
  appStatOptionsCache = null;
  appStatOptionsInflight = null;
}

/** sub_admin：从自身 userInfo 构建可授权的应用/渠道选项，无需请求全量列表 */
export function buildSubAdminStatOptions(userInfo: Api.Auth.UserInfo) {
  return {
    apps: (userInfo.appStatPerms ?? []).map(item => ({
      label: (item.appName || item.label || item.id) as string,
      value: String(item.id)
    })),
    channels: normalizeChannelList(userInfo.channelStatPerms).map(channel => ({
      label: channel,
      value: channel
    }))
  };
}
